'use client';

import { SliderArrow } from '@/components/SliderArrow';
import { Default_Image, base64Image } from '@/constants';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { IColumnBannerProperty } from '@/interfaces/homepage';
import { useEffect, useState } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';
import Image from 'next/image';
import CustomLink from '@/components/CustomLink';

const MultiColumnBanner = ({
  columnBannerItems,
  className = '',
  autoPlayTimer,
  dataTestId = 'multi-column-banner',
}: {
  columnBannerItems?: IColumnBannerProperty[];
  className?: string;
  autoPlayTimer: number;
  dataTestId?: string;
}) => {
  const [swiper, setSwiper] = useState<any>(null);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) containerRef?.current?.swiper?.autoplay.start();
    else containerRef?.current?.swiper?.autoplay.stop();
  }, [isVisible, containerRef]);

  return (
    <div className={`${className} relative`} data-testid={dataTestId}>
      <Swiper
        ref={containerRef}
        onSwiper={(s) => {
          setSwiper(s);
        }}
        modules={[Navigation, Autoplay]}
        slidesPerView={1}
        spaceBetween={20}
        autoplay={{
          delay: autoPlayTimer - 1000,
        }}
        breakpoints={{
          768: {
            slidesPerView: 2,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
          1200: {
            slidesPerView: 3,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
        }}
        navigation={{
          prevEl: '.mc-swipe-prev',
          nextEl: '.mc-swipe-next',
        }}
        loop
        data-testid={`${dataTestId}-swiper`}
      >
        {columnBannerItems?.map((item, index) => (
          <SwiperSlide key={item.Url} data-testid={`${dataTestId}-slide-${index}`}>
            <CustomLink href={item?.Url || ''} className="flex h-full flex-col items-end" data-testid={`${dataTestId}-link-${index}`}>
              <div className="relative aspect-[53/65] w-full grow object-none" data-testid={`${dataTestId}-image-container-${index}`}>
                <Image
                  src={item?.Image !== '' ? item?.Image : Default_Image}
                  alt={item?.AltText ? item?.AltText : 'image'}
                  fill
                  placeholder="blur"
                  blurDataURL={base64Image}
                  sizes="10%"
                  style={{
                    objectFit: 'cover',
                    aspectRatio: '53/65',
                    objectPosition: 'center',
                  }}
                  unoptimized={true}
                  data-testid={`${dataTestId}-image-${index}`}
                />
              </div>
            </CustomLink>
            <div
              className="relative mx-8 -mt-20 w-[calc(100%-4rem)] bg-white p-6"
              dangerouslySetInnerHTML={{ __html: item?.Text }}
              data-testid={`${dataTestId}-text-${index}`}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="arrow-design mc-swipe-prev swipe-prev my-auto" data-testid={`${dataTestId}-prev-arrow`}>
        <SliderArrow className="left-3 top-[200px]" data-testid={`${dataTestId}-prev-arrow-button`}>
          <IconArrowLeftDouble className="h-8 w-8" />
        </SliderArrow>
      </div>
      <div className="arrow-design mc-swipe-next swipe-next my-auto" data-testid={`${dataTestId}-next-arrow`}>
        <SliderArrow className="right-3 top-[200px]" data-testid={`${dataTestId}-next-arrow-button`}>
          <IconArrowRightDouble className="h-8 w-8" />
        </SliderArrow>
      </div>
      <style jsx>{`
        @media (min-width: 401px) {
          .arrow-design {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default MultiColumnBanner;
